import { useMemo } from "react";

import type { ProgramApiFetch } from "../ProgramEditor/types";
import { useProfileNames } from "../primitives/useProfileNames";
import { personalCreatedBy, type LibraryItem } from "./useLibrary";

// PE-48 review fix #7 — LibraryCoachBadge.tsx used to call the shared
// useProfileNames primitive itself, once PER CARD. That hook's cache is
// per-MOUNT (a useRef inside it), so eight programs assigned by the same
// coach meant eight separate <LibraryCoachBadge> instances, each running
// its own useProfileNames([coachSub]) — eight identical
// GET /api/profiles/:sub reads every time the Create-hub library opens
// (useProfileNames is already batched PER CALL; it was just never shared
// ACROSS the many calls one screen of cards makes).
//
// This hook does the batching a single card can't: ONE useProfileNames
// call, over every DISTINCT coach sub a currently-loaded item was assigned
// by. LibraryBrowser calls this once and hands the result down through
// buildLibraryItemCardProps → LibraryItemCard → LibraryCoachBadge's
// `presetName`, which skips that component's own fetch entirely once a
// name (or a still-unresolved null) is available from here.
export function useAssignedByCoachNames(
  apiFetch: ProgramApiFetch,
  items: ReadonlyArray<LibraryItem>,
  callerSub: string | null,
): Record<string, string> {
  // `callerSub === null` ⇒ we can't yet tell "assigned by someone else"
  // from "mine" (same guard buildLibraryItemCardProps.ts uses for
  // `assignedByCoachSub`) — resolve nothing rather than guess.
  const coachSubs = useMemo(() => {
    if (callerSub === null) return [];
    const seen = new Set<string>();
    for (const item of items) {
      const createdBy = personalCreatedBy(item);
      if (createdBy !== null && createdBy !== callerSub) seen.add(createdBy);
    }
    return [...seen];
  }, [items, callerSub]);

  return useProfileNames(apiFetch, coachSubs);
}
